/* =====================================================================
 * @auw/tenant — React context (<TenantProvider> + useTenant).
 *
 * Mounted at the application root so every descendant can reach the
 * active `TenantManifest` without threading it through props.  Pair
 * with `<FeatureFlagProvider>` from `@auw/feature-flags`, typically
 * like so:
 *
 *   <TenantProvider manifest={activeTenant}>
 *     <FeatureFlagProvider flags={activeTenant.features}>
 *       <App />
 *     </FeatureFlagProvider>
 *   </TenantProvider>
 * ===================================================================== */

import React, { createContext, useContext } from 'react';
import type { TenantManifest } from './types';

const TenantContext = createContext<TenantManifest | null>(null);

export interface TenantProviderProps {
  manifest: TenantManifest;
  children: React.ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({
  manifest,
  children,
}) => (
  <TenantContext.Provider value={manifest}>{children}</TenantContext.Provider>
);

/**
 * Access the active manifest.  Throws when called outside a
 * `<TenantProvider>` so a missing provider never silently yields the
 * default manifest — it has to be an explicit decision.
 */
export function useTenant(): TenantManifest {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error(
      '[tenant] useTenant must be used inside a <TenantProvider>.',
    );
  }
  return ctx;
}
